import { Autocomplete, Button, Input } from '@fullscreendigital/carrefour-dex-fe'
import { useMutation, useQuery } from 'react-query'
import { makeCoreGetRequest, makeCorePostRequest } from 'utils/api'
import { CircularProgress, Grid, Typography } from '@mui/material'
import { useNotification } from 'hooks'
import { formatErrorMessage } from 'utils/formatting'
import { useLocation, useNavigate } from 'react-router-dom'
import { URLS } from 'components/shop-reception/mobile/constants'
import { useMemo, useState } from 'react'
import ImageUpload from 'components/shop-reception/mobile/Common/ImageUpload/ImageUpload'
import { useFormik } from 'formik'
import { TitleHeader } from 'components/shop-reception/mobile/Common/TitleHeader'
import s from '../../../../Shop/GoodsReception/ListTrucks/TruckDetail/TruckDetail.modules.scss'
import { toApproveTruckPayload } from './serializers'
import { AxiosError } from 'axios'
import { useOfflineTool } from 'components/core-ui/Offline'

const ExpectedTrucksStep2 = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { showNotification } = useNotification()

  const item = location.state.item

  const [hasSealImage, setHasSealImage] = useState(false)
  const [hasThermodiagramImage, setHasThermodiagramImage] = useState(false)

  const offlineTool = useOfflineTool()

  const { data: truck, isLoading: isTruckLoading } = useQuery(
    ['truck'],
    async () => {
      const response = await makeCoreGetRequest(`/shop-reception-mobile/truck/hub/${item.value}`)

      await formik.setValues({
        temperature: null,
        thermodiagramConform: true,
        sealList: response.sealList?.map(seal => ({
          id: seal.id,
          code: seal.code,
          integral: true
        })),
        depositDocumentList: response.depositDocumentList?.map(doc => ({
          depositId: doc.depositId,
          depositName: doc.depositName,
          hasNotice: true,
          hasTransportNote: true
        }))
      })

      return response
    },
    {
      onError: error => {
        showNotification({
          message: `A aparut o eroare. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const approveTruck = async values => {
    await makeCorePostRequest(`/shop-reception-mobile/truck/hub/approve`, values)
  }

  const { isLoading, mutate } = useMutation(approveTruck, {
    onSuccess: () => {
      showNotification({
        message: `Confirmat`,
        type: 'success'
      })
      navigate(URLS.AUTH_EXPECTED_TRUCKS_STEP3, { state: { item: item.value } })
    },
    onError: (err: AxiosError) => {
      if (err.code === AxiosError.ERR_NETWORK) {
        // Store offline
        offlineTool.addOwnTask({
          type: `Adauga imagine`,
          data: err.config.data as FormData,
          url: `/shop-reception-mobile/truck/hub/approve`,
          method: 'post'
        })
      } else {
        showNotification({
          message: `A aparut o eroare. ${formatErrorMessage(err)}`,
          type: 'error'
        })
      }
    }
  })

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      temperature: null,
      thermodiagramConform: true,
      sealList: [{ id: '', code: '', integral: true }],
      documentList: [{ depositId: null, depositName: '', hasNotice: true, hasTransportNote: true }]
    },
    onSubmit: async (values: any) => {
      mutate(toApproveTruckPayload(item, values))
    }
  })

  const approveDisabled = useMemo(
    () =>
      (!!formik.values?.sealList?.length && !hasSealImage) ||
      !(formik.values?.thermodiagramConform === null || hasThermodiagramImage),
    [
      hasSealImage,
      formik.values?.sealList,
      formik.values?.thermodiagramConform,
      hasThermodiagramImage
    ]
  )

  if (isTruckLoading || isLoading || typeof formik.values === 'undefined') {
    return (
      <div style={{ display: 'flex' }}>
        <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
      </div>
    )
  }

  return (
    <Grid container spacing={2} justifyContent={'center'}>
      {truck && (
        <>
          <Grid xs={10} item display="flex" justifyContent="center">
            <Typography
              component="p"
              variant="medium"
              style={{ textAlign: 'center', color: 'black' }}
            >
              Numar inmatriculare: {truck.truckNumber} / {truck.transportCode}
            </Typography>
          </Grid>
          <Grid xs={10} item display="flex" justifyContent="center">
            <Typography
              component="p"
              variant="medium"
              style={{ textAlign: 'center', color: 'black' }}
            >
              Bon livrare: {truck.ticketCodeList.join(', ')}
            </Typography>
          </Grid>
        </>
      )}

      <Grid xs={10} item>
        {formik.values.sealList &&
          formik.values.sealList?.map((seal, index) => (
            <Grid key={index} item xs={12}>
              <Grid container>
                <Grid item xs={12}>
                  <Input
                    type="select"
                    label={`Confirm Sigiliu ${seal.code}`}
                    variant="outlined"
                    width="100%"
                    value={seal.integral}
                    values={[
                      { value: 'true', label: 'DA' },
                      { value: 'false', label: 'NU' }
                    ]}
                    onChange={event =>
                      formik.setFieldValue(
                        `sealList[${index}].integral`,
                        (event.target as HTMLInputElement).value
                      )
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <ImageUpload
                    uploadUrl={`/shop-reception-mobile/truck/hub/upload/in_seal/${item.value}`}
                    onSuccess={() => {
                      showNotification({
                        message: `Imaginea a fost incarcata cu succes.`,
                        type: 'success'
                      })
                      setHasSealImage(true)
                    }}
                    onError={() =>
                      showNotification({
                        message: `A aparut o eroare la incarcarea imaginii.`,
                        type: 'error'
                      })
                    }
                    onInternetConnectionError={() => setHasSealImage(true)}
                  />
                </Grid>
              </Grid>
            </Grid>
          ))}
      </Grid>
      <Grid xs={10} item>
        {formik.values.depositDocumentList &&
          formik.values.depositDocumentList?.map((document, idx) => (
            <Grid key={idx} item xs={12} container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="medium" component="p">
                  Confirm documentele pentru depozit {document.depositName}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Input
                  type="select"
                  label="Aviz/BL"
                  variant="outlined"
                  width="100%"
                  value={document.hasNotice || 'none'}
                  values={[
                    {
                      value: 'true',
                      label: 'DA'
                    },
                    {
                      value: 'false',
                      label: 'NU'
                    },
                    {
                      value: 'none',
                      label: 'NU E CAZUL'
                    }
                  ]}
                  onChange={event => {
                    formik.setFieldValue(
                      `depositDocumentList[${idx}].hasNotice`,
                      (event.target as HTMLInputElement).value === 'none'
                        ? null
                        : (event.target as HTMLInputElement).value
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Input
                  type="select"
                  label="Scrisoare de transport/CMR"
                  variant="outlined"
                  width="100%"
                  value={document.hasTransportNote || 'none'}
                  values={[
                    {
                      value: 'true',
                      label: 'DA'
                    },
                    {
                      value: 'false',
                      label: 'NU'
                    },
                    {
                      value: 'none',
                      label: 'NU E CAZUL'
                    }
                  ]}
                  onChange={event => {
                    formik.setFieldValue(
                      `depositDocumentList[${idx}].hasTransportNote`,
                      (event.target as HTMLInputElement).value === 'none'
                        ? null
                        : (event.target as HTMLInputElement).value
                    )
                  }}
                />
              </Grid>
            </Grid>
          ))}
      </Grid>
      <Grid item xs={10} className={s.imageContainer}>
        <Grid container>
          <Grid item xs={12}>
            <Input
              type="select"
              label="Conformitate temperatura marfa"
              variant="outlined"
              width="100%"
              value={formik.values.thermodiagramConform || 'none'}
              values={[
                {
                  value: 'true',
                  label: 'DA'
                },
                {
                  value: 'false',
                  label: 'NU'
                },
                {
                  value: 'none',
                  label: 'NU E CAZUL'
                }
              ]}
              onChange={event => {
                formik.setFieldValue(
                  'thermodiagramConform',
                  (event.target as HTMLInputElement).value === 'none'
                    ? null
                    : (event.target as HTMLInputElement).value
                )
              }}
            />
          </Grid>
          {formik.values.thermodiagramConform !== null && (
            <Grid item xs={12}>
              <ImageUpload
                uploadUrl={`/shop-reception-mobile/truck/hub/upload/in_thermodiagram/${item.value}`}
                onSuccess={() => {
                  showNotification({
                    message: `Imaginea a fost incarcata cu succes.`,
                    type: 'success'
                  })
                  setHasThermodiagramImage(true)
                }}
                onError={() =>
                  showNotification({
                    message: `A aparut o eroare la incarcarea imaginii.}`,
                    type: 'error'
                  })
                }
                onInternetConnectionError={() => setHasThermodiagramImage(true)}
              />
            </Grid>
          )}
        </Grid>
      </Grid>

      {formik.values.thermodiagramConform === 'false' && (
        <Grid xs={10} item>
          <Input
            type="number"
            variant="outlined"
            label="Temperatura"
            width="100%"
            value={formik.values.temperature}
            onChange={event =>
              formik.setFieldValue("temperature", (event.target as HTMLInputElement).value)
            }
          />
        </Grid>
      )}

      <Grid xs={10} item>
        <Button
          variant="contained"
          label="Pasul urmator"
          onClick={() => formik.handleSubmit()}
          style={{ width: '100%' }}
          disabled={approveDisabled || isLoading}
        />
      </Grid>
    </Grid>
  )
}

export default ExpectedTrucksStep2
